import React, { useState, useEffect } from 'react';
import { listReleases, createRelease, publishRelease, deleteRelease } from 'service/ncoreBoardService';
import { useAuth } from 'contexts/AuthContext';
import styles from './ReleasesTab.module.scss';

function timeAgo(dateStr) {
    if (!dateStr) return '';
    const d = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
    if (d === 0) return 'сегодня';
    if (d === 1) return 'вчера';
    if (d < 30) return `${d}д. назад`;
    return new Date(dateStr).toLocaleDateString('ru-RU');
}

function Avatar({ user, size = 28 }) {
    return user?.avatar
        ? <img src={user.avatar} alt={user.username} style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover' }} />
        : <div className={styles.avatarFallback} style={{ width: size, height: size, fontSize: size * 0.4 }}>
            {(user?.username || '?')[0].toUpperCase()}
          </div>;
}

export default function ReleasesTab({ repo, isOwner }) {
    const { user } = useAuth();
    const [releases, setReleases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ tag_name: '', name: '', body: '', is_prerelease: false, is_draft: false });

    useEffect(() => { loadReleases(); }, [repo.id]);

    async function loadReleases() {
        setLoading(true);
        try {
            const data = await listReleases(repo.id);
            setReleases(data.results || data);
        } catch { setReleases([]); }
        setLoading(false);
    }

    async function handleCreate(e) {
        e.preventDefault();
        try {
            await createRelease(repo.id, form);
            setCreating(false);
            setForm({ tag_name: '', name: '', body: '', is_prerelease: false, is_draft: false });
            loadReleases();
        } catch (err) { alert(err.message); }
    }

    async function handlePublish(releaseId) {
        try {
            await publishRelease(repo.id, releaseId);
            loadReleases();
        } catch (err) { alert(err.message); }
    }

    async function handleDelete(releaseId) {
        if (!window.confirm('Удалить этот релиз?')) return;
        try {
            await deleteRelease(repo.id, releaseId);
            loadReleases();
        } catch (err) { alert(err.message); }
    }

    return (
        <div className={styles.wrap}>
            <div className={styles.header}>
                <h3 className={styles.sectionTitle}>
                    Релизы
                    {releases.length > 0 && <span className={styles.badge}>{releases.length}</span>}
                </h3>
                {user && isOwner && (
                    <button className={styles.newBtn} onClick={() => setCreating(true)}>
                        + Создать релиз
                    </button>
                )}
            </div>

            {creating && (
                <form className={styles.createForm} onSubmit={handleCreate}>
                    <h3>Новый релиз</h3>
                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label>Тег *</label>
                            <input
                                className={styles.inputField}
                                placeholder="v1.0.0"
                                value={form.tag_name}
                                onChange={e => setForm(p => ({ ...p, tag_name: e.target.value }))}
                                required
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <label>Название</label>
                            <input
                                className={styles.inputField}
                                placeholder="Release v1.0.0"
                                value={form.name}
                                onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                            />
                        </div>
                    </div>
                    <div className={styles.formGroup}>
                        <label>Описание (Release notes)</label>
                        <textarea
                            className={styles.textareaField}
                            placeholder="Опишите изменения в этом релизе..."
                            value={form.body}
                            onChange={e => setForm(p => ({ ...p, body: e.target.value }))}
                            rows={5}
                        />
                    </div>
                    <div className={styles.checkboxRow}>
                        <label className={styles.checkboxLabel}>
                            <input type="checkbox" checked={form.is_prerelease} onChange={e => setForm(p => ({ ...p, is_prerelease: e.target.checked }))} />
                            Pre-release
                        </label>
                        <label className={styles.checkboxLabel}>
                            <input type="checkbox" checked={form.is_draft} onChange={e => setForm(p => ({ ...p, is_draft: e.target.checked }))} />
                            Черновик
                        </label>
                    </div>
                    <div className={styles.formActions}>
                        <button type="button" className={styles.cancelBtn} onClick={() => setCreating(false)}>Отмена</button>
                        <button type="submit" className={styles.submitBtn}>
                            {form.is_draft ? 'Сохранить черновик' : 'Опубликовать релиз'}
                        </button>
                    </div>
                </form>
            )}

            {loading ? (
                <div className={styles.loading}>Загрузка...</div>
            ) : releases.length === 0 ? (
                <div className={styles.empty}>
                    <span style={{ fontSize: 48 }}>🏷️</span>
                    <p>Нет релизов</p>
                    <span>Создайте первый релиз чтобы опубликовать версию</span>
                </div>
            ) : (
                <div className={styles.releaseList}>
                    {releases.map((release, idx) => (
                        <div key={release.id} className={`${styles.releaseCard} ${release.is_draft ? styles.draftCard : ''}`}>
                            <div className={styles.releaseLeft}>
                                <div className={styles.tagCol}>
                                    <span className={styles.tagIcon}>🏷️</span>
                                    <span className={styles.tagName}>{release.tag_name}</span>
                                </div>
                            </div>
                            <div className={styles.releaseContent}>
                                <div className={styles.releaseHeader}>
                                    <div className={styles.releaseTitleRow}>
                                        <h3 className={styles.releaseTitle}>{release.name || release.tag_name}</h3>
                                        {idx === 0 && !release.is_draft && !release.is_prerelease && (
                                            <span className={styles.latestBadge}>Последний</span>
                                        )}
                                        {release.is_prerelease && (
                                            <span className={styles.preBadge}>Pre-release</span>
                                        )}
                                        {release.is_draft && (
                                            <span className={styles.draftBadge}>Черновик</span>
                                        )}
                                    </div>
                                    <div className={styles.releaseMeta}>
                                        <Avatar user={release.author} size={20} />
                                        <span>{release.author?.username}</span>
                                        {release.published_at
                                            ? <span>опубликовано {timeAgo(release.published_at)}</span>
                                            : <span>создано {timeAgo(release.created_at)}</span>
                                        }
                                    </div>
                                </div>
                                {release.body && (
                                    <pre className={styles.releaseBody}>{release.body}</pre>
                                )}
                                <div className={styles.releaseFooter}>
                                    <a
                                        className={styles.downloadLink}
                                        href={`https://security.ncore.kg/api/ncoreboard/repos/${repo.id}/releases/${release.id}/download/`}
                                        onClick={e => e.stopPropagation()}
                                    >
                                        ↓ Source code (zip)
                                    </a>
                                    {isOwner && (
                                        <div className={styles.releaseActions}>
                                            {release.is_draft && (
                                                <button className={styles.publishBtn} onClick={() => handlePublish(release.id)}>
                                                    Опубликовать
                                                </button>
                                            )}
                                            <button className={styles.deleteBtn} onClick={() => handleDelete(release.id)}>
                                                Удалить
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
